import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

/**
 * Connector Type SVG Icons
 */
const ConnectorTypeIcons = {
  Type2: ({ className = "" }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor">
      {/* Type 2 (Mennekes) connector icon */}
      <path d="M16 2C9.373 2 4 7.373 4 14v4c0 6.627 5.373 12 12 12s12-5.373 12-12v-4c0-6.627-5.373-12-12-12zm0 2c5.523 0 10 4.477 10 10v4c0 5.523-4.477 10-10 10S6 23.523 6 18v-4c0-5.523 4.477-10 10-10z"/>
      <circle cx="10" cy="12" r="2"/>
      <circle cx="16" cy="10" r="2"/>
      <circle cx="22" cy="12" r="2"/>
      <circle cx="11" cy="18" r="2"/>
      <circle cx="16" cy="20" r="2"/>
      <circle cx="21" cy="18" r="2"/>
      <rect x="14" y="2" width="4" height="4" rx="1"/>
    </svg>
  ),
  
  CCS: ({ className = "" }) => (
    <svg viewBox="0 0 32 40" className={className} fill="currentColor">
      {/* CCS Combo 2 connector icon */}
      <path d="M16 2C10.477 2 6 6.477 6 12v2c0 5.523 4.477 10 10 10s10-4.477 10-10v-2c0-5.523-4.477-10-10-10zm0 2c4.418 0 8 3.582 8 8v2c0 4.418-3.582 8-8 8s-8-3.582-8-8v-2c0-4.418 3.582-8 8-8z"/>
      <circle cx="11" cy="10" r="1.5"/>
      <circle cx="16" cy="9" r="1.5"/>
      <circle cx="21" cy="10" r="1.5"/>
      <circle cx="12" cy="15" r="1.5"/>
      <circle cx="16" cy="16" r="1.5"/>
      <circle cx="20" cy="15" r="1.5"/>
      <rect x="14" y="2" width="4" height="3" rx="1"/>
      {/* DC pins below */}
      <rect x="8" y="26" width="6" height="10" rx="2" />
      <rect x="18" y="26" width="6" height="10" rx="2" />
      <rect x="10" y="28" width="2" height="6" fill="currentColor" opacity="0.5"/>
      <rect x="20" y="28" width="2" height="6" fill="currentColor" opacity="0.5"/>
    </svg>
  ),
  
  CHAdeMO: ({ className = "" }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor">
      {/* CHAdeMO connector icon */}
      <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="2"/>
      <circle cx="16" cy="16" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="11" cy="11" r="2.5"/>
      <circle cx="21" cy="11" r="2.5"/>
      <circle cx="11" cy="21" r="2.5"/>
      <circle cx="21" cy="21" r="2.5"/>
      <circle cx="16" cy="16" r="2"/>
    </svg>
  ),
  
  GBT: ({ className = "" }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor">
      {/* GB/T connector icon */}
      <rect x="4" y="4" width="24" height="24" rx="4" fill="none" stroke="currentColor" strokeWidth="2"/>
      <circle cx="10" cy="10" r="2"/>
      <circle cx="22" cy="10" r="2"/>
      <circle cx="10" cy="22" r="2"/>
      <circle cx="22" cy="22" r="2"/>
      <circle cx="16" cy="16" r="3"/>
    </svg>
  ),
  
  Tesla: ({ className = "" }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor">
      {/* Tesla connector icon */}
      <rect x="6" y="2" width="20" height="28" rx="4" fill="none" stroke="currentColor" strokeWidth="2"/>
      <circle cx="12" cy="10" r="2"/>
      <circle cx="20" cy="10" r="2"/>
      <circle cx="12" cy="18" r="2"/>
      <circle cx="20" cy="18" r="2"/>
      <circle cx="16" cy="26" r="2"/>
    </svg>
  )
};

// Get connector icon component
const getConnectorIcon = (connectorType) => {
  const type = (connectorType || 'Type2').replace(/[\s-]/g, '');
  return ConnectorTypeIcons[type] || ConnectorTypeIcons.Type2;
};

/**
 * OCPP Connector Status Colors and Labels
 */
const CONNECTOR_STATUS_CONFIG = {
  Available: {
    bgColor: 'bg-green-500',
    glowColor: 'rgba(34, 197, 94, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-green-400',
    label: 'E Lirë',
    canGlow: false
  },
  Preparing: {
    bgColor: 'bg-orange-500',
    glowColor: 'rgba(249, 115, 22, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-orange-400',
    label: 'Në Përgatitje',
    canGlow: false
  },
  Charging: {
    bgColor: 'bg-red-500',
    glowColor: 'rgba(239, 68, 68, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-red-400',
    label: 'Në Karikim',
    canGlow: true
  },
  SuspendedEV: {
    bgColor: 'bg-orange-500',
    glowColor: 'rgba(249, 115, 22, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-orange-400',
    label: 'Pezulluar (EV)',
    canGlow: false
  },
  SuspendedEVSE: {
    bgColor: 'bg-orange-500',
    glowColor: 'rgba(249, 115, 22, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-orange-400',
    label: 'Pezulluar (EVSE)',
    canGlow: false
  },
  Finishing: {
    bgColor: 'bg-blue-400',
    glowColor: 'rgba(96, 165, 250, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-blue-300',
    label: 'Duke Përfunduar',
    canGlow: false
  },
  Faulted: {
    bgColor: 'bg-red-500',
    glowColor: 'rgba(239, 68, 68, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-red-400',
    label: 'Defekt',
    canGlow: false
  },
  Unavailable: {
    bgColor: 'bg-zinc-500',
    glowColor: 'rgba(113, 113, 122, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-zinc-400',
    label: 'Jo E Disponueshme',
    canGlow: false
  },
  Offline: {
    bgColor: 'bg-zinc-600',
    glowColor: 'rgba(82, 82, 91, VAR_OPACITY)',
    textColor: 'text-zinc-300',
    borderColor: 'border-zinc-500',
    label: 'Offline',
    canGlow: false
  },
  Reserved: {
    bgColor: 'bg-purple-500',
    glowColor: 'rgba(168, 85, 247, VAR_OPACITY)',
    textColor: 'text-white',
    borderColor: 'border-purple-400',
    label: 'E Rezervuar',
    canGlow: false
  }
};

// Glow intensity parameters
const GLOW_CONFIG = {
  minOpacity: 0.25,
  maxOpacity: 0.95,
  minBlur: 6,
  maxBlur: 18,
  minSpread: 0,
  maxSpread: 6,
  minPulseDuration: 2.2,
  maxPulseDuration: 0.8,
  smoothingFactor: 0.15,
  deadband: 0.03,
  minPowerThreshold: 0.2,
  updateInterval: 500 // Faster updates for live kW display
};

// Utility function to convert connector_id to letter (1->A, 2->B, etc.)
export const getConnectorLabel = (connectorId) => {
  if (connectorId === 0) return '0';
  if (typeof connectorId !== 'number' || connectorId < 1) return String(connectorId);
  return String.fromCharCode(64 + connectorId); // 1->A, 2->B, 3->C
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const calculateGlowParams = (currentPowerKw, maxPowerKw) => {
  if (currentPowerKw <= GLOW_CONFIG.minPowerThreshold || maxPowerKw <= 0) {
    return {
      normalized: 0,
      opacity: GLOW_CONFIG.minOpacity,
      blur: GLOW_CONFIG.minBlur,
      spread: GLOW_CONFIG.minSpread,
      pulseDuration: GLOW_CONFIG.minPulseDuration,
      isActive: false
    };
  }

  const normalized = clamp(currentPowerKw / maxPowerKw, 0, 1);
  
  return {
    normalized,
    opacity: GLOW_CONFIG.minOpacity + normalized * (GLOW_CONFIG.maxOpacity - GLOW_CONFIG.minOpacity),
    blur: GLOW_CONFIG.minBlur + normalized * (GLOW_CONFIG.maxBlur - GLOW_CONFIG.minBlur),
    spread: GLOW_CONFIG.minSpread + normalized * (GLOW_CONFIG.maxSpread - GLOW_CONFIG.minSpread),
    pulseDuration: GLOW_CONFIG.minPulseDuration - normalized * (GLOW_CONFIG.minPulseDuration - GLOW_CONFIG.maxPulseDuration),
    isActive: true
  };
};

/**
 * Hook for smoothed power value with EMA - for live kW updates
 */
const useSmoothedPower = (currentPower, maxPower) => {
  const smoothedRef = useRef(currentPower);
  const lastUpdateRef = useRef(Date.now());
  const [displayPower, setDisplayPower] = useState(currentPower);
  const [glowParams, setGlowParams] = useState(() => calculateGlowParams(currentPower, maxPower));

  useEffect(() => {
    // Update display power immediately for live updates
    setDisplayPower(currentPower);
    
    const now = Date.now();
    
    // Rate limit glow updates (but not display power)
    if (now - lastUpdateRef.current < GLOW_CONFIG.updateInterval) {
      return;
    }

    // Apply EMA smoothing for glow
    const alpha = GLOW_CONFIG.smoothingFactor;
    smoothedRef.current = alpha * currentPower + (1 - alpha) * smoothedRef.current;
    
    const newParams = calculateGlowParams(smoothedRef.current, maxPower);
    
    // Deadband check for glow params
    if (Math.abs(newParams.normalized - glowParams.normalized) > GLOW_CONFIG.deadband || 
        newParams.isActive !== glowParams.isActive) {
      setGlowParams(newParams);
      lastUpdateRef.current = now;
    }
  }, [currentPower, maxPower, glowParams.normalized, glowParams.isActive]);

  return { displayPower, glowParams };
};

/**
 * Single connector badge with icon and Lightning Ring effect
 */
const ConnectorBadge = ({ connector, isChargerOnline, maxPowerKw = 22, chargerStatus, compact = false }) => {
  // Connector should show as Offline if:
  // 1. Charger is not online (isChargerOnline = false)
  // 2. Charger status is Offline or Reconnecting
  const isEffectivelyOffline = !isChargerOnline || chargerStatus === 'Offline' || chargerStatus === 'Reconnecting';
  const effectiveStatus = isEffectivelyOffline ? 'Offline' : (connector.status || 'Available');
  const config = CONNECTOR_STATUS_CONFIG[effectiveStatus] || CONNECTOR_STATUS_CONFIG.Available;
  const label = getConnectorLabel(connector.connector_id);
  const currentPower = connector.current_power_kw || 0;
  const connectorType = connector.connector_type || 'Type2';
  
  // Get connector icon component
  const ConnectorIcon = getConnectorIcon(connectorType);
  
  // Get smoothed glow parameters and live display power
  const { displayPower, glowParams } = useSmoothedPower(currentPower, maxPowerKw);
  
  // Determine if we should show the glow effect
  const showGlow = !compact && config.canGlow && isChargerOnline && effectiveStatus === 'Charging' && glowParams.isActive;
  
  // Build dynamic styles
  const dynamicStyles = useMemo(() => {
    if (!showGlow) return {};
    
    const glowColor = config.glowColor.replace('VAR_OPACITY', glowParams.opacity.toFixed(2));
    
    return {
      '--pulse-duration': `${glowParams.pulseDuration.toFixed(2)}s`,
      '--glow-opacity': glowParams.opacity.toFixed(2),
      '--glow-blur': `${glowParams.blur.toFixed(0)}px`,
      '--glow-spread': `${glowParams.spread.toFixed(0)}px`,
      '--glow-color': glowColor,
      boxShadow: `0 0 ${glowParams.blur.toFixed(0)}px ${glowParams.spread.toFixed(0)}px ${glowColor}`
    };
  }, [showGlow, config.glowColor, glowParams]);

  const badgeInner = (
    <div 
      className="connector-badge-wrapper relative inline-flex items-center"
      data-testid={`connector-badge-${connector.connector_id}`}
      data-status={effectiveStatus}
      data-power={displayPower.toFixed(1)}
    >
      {!compact && (
        <div className="connector-type-icon absolute -left-2 -top-1 opacity-30 z-0">
          <ConnectorIcon className="w-12 h-12 text-zinc-300" />
        </div>
      )}
      
      <div 
        className={`
          connector-badge
          relative z-10 inline-flex items-center justify-center
          ${compact ? 'min-w-[28px] h-[28px] px-2 ml-0 text-xs' : 'min-w-[36px] h-[36px] px-3 ml-6 text-sm'}
          rounded-full font-bold
          ${config.bgColor} ${config.textColor}
          border-2 ${config.borderColor}
          shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)]
          transition-all duration-300 cursor-default
          ${compact ? '' : 'hover:scale-105'}
          ${showGlow ? 'connector--charging' : ''}
        `}
        style={dynamicStyles}
      >
        {showGlow && (
          <span 
            className="connector-glow-ring absolute inset-[-4px] rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${config.glowColor.replace('VAR_OPACITY', (glowParams.opacity * 0.5).toFixed(2))} 0%, transparent 70%)`,
              animation: `connectorPulse var(--pulse-duration, 2s) ease-in-out infinite`,
              opacity: glowParams.opacity
            }}
          />
        )}
        
        {showGlow && (
          <span 
            className="connector-pulse-ring absolute inset-0 rounded-full pointer-events-none"
            style={{
              border: `2px solid ${config.glowColor.replace('VAR_OPACITY', (glowParams.opacity * 0.7).toFixed(2))}`,
              animation: `connectorRingPulse var(--pulse-duration, 2s) ease-out infinite`
            }}
          />
        )}
        
        <span className="relative z-10 drop-shadow-sm">{label}</span>
        
        {!compact && displayPower > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-yellow-400 border-2 border-background"
            style={{
              animation: showGlow ? `connectorDotPulse var(--pulse-duration, 2s) ease-in-out infinite` : 'none'
            }}
          />
        )}
      </div>
      
      {!compact && showGlow && displayPower > 0 && (
        <div 
          className="charging-live-info absolute left-1/2 -translate-x-1/2 top-full mt-1 z-20 
            bg-zinc-900/95 border border-red-500/50 rounded-lg px-2 py-1.5 min-w-[80px]
            shadow-lg shadow-red-500/20 backdrop-blur-sm"
          style={{ animation: 'fadeIn 0.3s ease-out' }}
        >
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1 text-red-400 font-bold text-sm tabular-nums">
              <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"/>
              </svg>
              {displayPower.toFixed(1)} kW
            </div>
            {connector.soc_percent != null && (
              <div className="flex items-center gap-1 text-[10px] text-zinc-400">
                <span className="text-emerald-400 font-medium">{connector.soc_percent}%</span>
                <span>SoC</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (compact) {
    return badgeInner;
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeInner}
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="bg-zinc-900 border-zinc-700 text-white p-3 min-w-[180px]"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ConnectorIcon className="w-5 h-5 text-zinc-400" />
              <span 
                className={`w-3 h-3 rounded-full ${config.bgColor}`}
                style={showGlow ? { boxShadow: `0 0 8px 2px ${config.glowColor.replace('VAR_OPACITY', '0.6')}` } : {}}
              />
              <span className="font-bold">Porta {label}</span>
            </div>
            <div className="text-xs text-zinc-400 space-y-1">
              <div className="flex justify-between">
                <span>Statusi:</span>
                <span className={config.textColor}>{config.label}</span>
              </div>
              <div className="flex justify-between">
                <span>Tipi:</span>
                <span>{connectorType}</span>
              </div>
              {displayPower > 0 && (
                <>
                  <div className="flex justify-between text-green-400 font-medium pt-1 border-t border-zinc-700 mt-1">
                    <span>Fuqia:</span>
                    <span className="tabular-nums">{displayPower.toFixed(1)} kW</span>
                  </div>
                  {showGlow && (
                    <div className="flex justify-between text-zinc-500 text-[10px]">
                      <span>Intensiteti:</span>
                      <span>{(glowParams.normalized * 100).toFixed(0)}%</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
      
      {/* Keyframe animations */}
      <style>{`
        @keyframes connectorPulse {
          0%, 100% {
            transform: scale(1);
            opacity: var(--glow-opacity, 0.5);
          }
          50% {
            transform: scale(1.15);
            opacity: calc(var(--glow-opacity, 0.5) * 0.6);
          }
        }
        
        @keyframes connectorRingPulse {
          0% {
            transform: scale(1);
            opacity: var(--glow-opacity, 0.7);
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }
        
        @keyframes connectorDotPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 4px 1px rgba(250, 204, 21, 0.5);
          }
          50% {
            transform: scale(1.2);
            box-shadow: 0 0 8px 2px rgba(250, 204, 21, 0.8);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        
        .charging-live-info {
          animation: fadeIn 0.3s ease-out, pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
          }
          50% {
            box-shadow: 0 4px 25px rgba(239, 68, 68, 0.5);
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .connector--charging .connector-glow-ring,
          .connector--charging .connector-pulse-ring,
          .connector--charging span,
          .charging-live-info {
            animation: none !important;
          }
          
          .connector--charging {
            box-shadow: 0 0 8px 2px var(--glow-color, rgba(239, 68, 68, 0.4));
          }
        }
      `}</style>
    </TooltipProvider>
  );
};

/**
 * Connector badges display for charger list
 */
export const ConnectorBadges = ({ connectors = [], isChargerOnline = true, maxPowerKw = 22, maxDisplay = 4, chargerStatus = null, compact = false }) => {
  if (!connectors || connectors.length === 0) {
    return <span className="text-zinc-500 text-sm">-</span>;
  }
  
  const sortedConnectors = [...connectors].sort((a, b) => a.connector_id - b.connector_id);
  const displayConnectors = sortedConnectors.slice(0, maxDisplay);
  const remaining = sortedConnectors.length - maxDisplay;
  
  return (
    <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-3'}`} data-testid="connector-badges">
      {displayConnectors.map((conn) => (
        <ConnectorBadge 
          key={conn.id || conn.connector_id} 
          connector={conn} 
          isChargerOnline={isChargerOnline}
          maxPowerKw={maxPowerKw}
          chargerStatus={chargerStatus}
          compact={compact}
        />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-zinc-500 ml-1">+{remaining}</span>
      )}
    </div>
  );
};

/**
 * Power display with progress bar - with live kW updates
 */
export const ConnectorPowerDisplay = ({ connectors = [], maxPowerKw = 0, isChargerOnline = true }) => {
  // Calculate total power from all connectors - updates live
  const totalActivePower = connectors.reduce((sum, c) => sum + (c.current_power_kw || 0), 0);
  const powerPercentage = maxPowerKw > 0 ? (totalActivePower / maxPowerKw) * 100 : 0;
  const hasActivePower = totalActivePower > 0;
  const isCharging = connectors.some(c => c.status === 'Charging');
  
  const glowParams = useMemo(() => {
    if (!hasActivePower || !isCharging) return null;
    return calculateGlowParams(totalActivePower, maxPowerKw);
  }, [totalActivePower, maxPowerKw, hasActivePower, isCharging]);
  
  const sortedConnectors = [...connectors].sort((a, b) => a.connector_id - b.connector_id);
  
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className="flex flex-col gap-1 min-w-[100px] cursor-default"
            data-testid="connector-power-display"
          >
            {/* Power text - updates live */}
            <div className={`text-sm font-medium tabular-nums ${hasActivePower ? 'text-green-400' : 'text-foreground'}`}>
              {totalActivePower.toFixed(1)} / {maxPowerKw} kW
            </div>
            
            {/* Progress bar with glow */}
            <div className="relative w-full h-2 bg-zinc-700 rounded-full overflow-visible border border-zinc-600">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  hasActivePower 
                    ? 'bg-gradient-to-r from-green-500 to-green-400' 
                    : 'bg-zinc-600'
                }`}
                style={{ 
                  width: `${Math.min(powerPercentage, 100)}%`,
                  boxShadow: glowParams?.isActive 
                    ? `0 0 ${glowParams.blur}px ${glowParams.spread}px rgba(74, 222, 128, ${glowParams.opacity * 0.7})`
                    : 'none'
                }}
              />
              
              {/* Animated glow pulse on bar */}
              {glowParams?.isActive && (
                <div 
                  className="absolute inset-0 rounded-full pointer-events-none overflow-hidden"
                  style={{ width: `${Math.min(powerPercentage, 100)}%` }}
                >
                  <div 
                    className="h-full w-full"
                    style={{
                      background: `linear-gradient(90deg, transparent 40%, rgba(74, 222, 128, ${glowParams.opacity * 0.4}) 80%, transparent 100%)`,
                      animation: `powerBarGlow ${glowParams.pulseDuration}s ease-in-out infinite`
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="bg-zinc-900 border-zinc-700 text-white p-3"
        >
          <div className="space-y-2">
            <div className="font-medium text-sm mb-2">Fuqia për Portë</div>
            {sortedConnectors.length > 0 ? (
              <>
                {sortedConnectors.map((conn) => {
                  const label = getConnectorLabel(conn.connector_id);
                  const power = conn.current_power_kw || 0;
                  const statusConfig = CONNECTOR_STATUS_CONFIG[conn.status] || CONNECTOR_STATUS_CONFIG.Available;
                  const ConnIcon = getConnectorIcon(conn.connector_type);
                  
                  return (
                    <div key={conn.id || conn.connector_id} className="flex items-center justify-between gap-4 text-xs">
                      <div className="flex items-center gap-2">
                        <ConnIcon className="w-4 h-4 text-zinc-500" />
                        <span className={`w-2 h-2 rounded-full ${statusConfig.bgColor}`} />
                        <span>Porta {label}</span>
                      </div>
                      <span className={`tabular-nums ${power > 0 ? 'text-green-400 font-medium' : 'text-zinc-500'}`}>
                        {power.toFixed(1)} kW
                      </span>
                    </div>
                  );
                })}
                <div className="border-t border-zinc-700 pt-2 mt-2 flex justify-between text-xs font-medium">
                  <span>Total</span>
                  <span className={`tabular-nums ${totalActivePower > 0 ? 'text-green-400' : ''}`}>
                    {totalActivePower.toFixed(1)} / {maxPowerKw} kW
                  </span>
                </div>
                {glowParams?.isActive && (
                  <div className="text-[10px] text-zinc-500 text-center pt-1">
                    Intensiteti: {(glowParams.normalized * 100).toFixed(0)}%
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-zinc-500">Pa konektorë</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
      
      <style>{`
        @keyframes powerBarGlow {
          0%, 100% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(100%);
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .connector-power-display * {
            animation: none !important;
          }
        }
      `}</style>
    </TooltipProvider>
  );
};

/**
 * Get aggregate charger status based on connectors
 */
export const getAggregateStatus = (connectors, isOnline) => {
  if (!isOnline) return 'Offline';
  if (!connectors || connectors.length === 0) return 'Available';
  
  const statuses = connectors.map(c => c.status);
  
  if (statuses.every(s => s === 'Faulted')) return 'Faulted';
  if (statuses.some(s => s === 'Faulted')) return 'PartialFault';
  if (statuses.some(s => s === 'Charging')) return 'Charging';
  if (statuses.some(s => s === 'Preparing')) return 'Preparing';
  if (statuses.some(s => s === 'Finishing')) return 'Finishing';
  if (statuses.some(s => s === 'SuspendedEV' || s === 'SuspendedEVSE')) return 'Suspended';
  if (statuses.some(s => s === 'Available')) return 'Available';
  if (statuses.every(s => s === 'Unavailable')) return 'Unavailable';
  
  return 'Available';
};

export default ConnectorBadges;
